/**
 * The smallest router that could work.
 *
 * Three routes do not justify a dependency: the whole thing is a path parser
 * plus a `popstate` listener. It is SPA history routing (not hash routing)
 * because the viewer link has to look like `share.example.com/r/ABCDEF`, which
 * also means the static host needs an SPA fallback - Caddy gets
 * `try_files {path} /index.html` in Phase 4.
 *
 * Works identically inside the Tauri WebView2 window and in a browser, which is
 * the point: the same build serves `/host` to the streamer and `/r/CODE` to
 * their friends.
 */

import { parseRoomTarget, type RoomTarget } from './room-target'
import { defaultSignalUrl } from './signaling'
import { isMobileBuild } from './platform'
import { normalizeRoomCode } from './protocol'
import { SvelteURL } from 'svelte/reactivity'

export type Route =
  | { name: 'home' }
  | { name: 'host' }
  | { name: 'settings' }
  | { name: 'friends'; addId: string | null }
  | { name: 'mini' }
  | { name: 'overlay' }
  | { name: 'marks' }
  | { name: 'viewer'; code: string; target: RoomTarget }
  | { name: 'debug' }
  | { name: 'notFound'; path: string }

/** Strip trailing slashes, keeping the root path as `/`. */
function normalize(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '')
  return trimmed === '' ? '/' : trimmed
}

/** Turn a path into a route. Pure, so it is easy to reason about. */
export function parseRoute(pathname: string): Route {
  const local = new SvelteURL(pathname, 'https://router.invalid')
  const path = normalize(local.pathname)
  if (isMobileBuild && !['/', '/settings'].includes(path) && !path.startsWith('/r/')) {
    return { name: 'home' }
  }

  if (path === '/') return { name: 'home' }
  if (path === '/host') return { name: 'host' }
  if (path === '/settings') return { name: 'settings' }
  if (path === '/mini') return { name: 'mini' }
  if (path === '/overlay') return { name: 'overlay' }
  if (path === '/marks') return { name: 'marks' }
  // Where Supabase sends the browser back after Google or Discord sign-in.
  if (path === '/friends' || path === '/auth/callback') return { name: 'friends', addId: null }

  const add = /^\/add\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.exec(path)
  if (add) return { name: 'friends', addId: add[1].toLowerCase() }
  if (path === '/debug') return { name: 'debug' }

  const viewer = /^\/r\/([^/]+)$/.exec(path)
  if (viewer) {
    let code: string
    try {
      code = decodeURIComponent(viewer[1])
    } catch {
      // A malformed percent-escape in a hand-typed URL should show the "not
      // found" page, not throw during render.
      return { name: 'notFound', path }
    }
    try {
      const target = parseRoomTarget(
        local.searchParams.get('room') || `/r/${encodeURIComponent(code)}`,
        defaultSignalUrl(),
        !isMobileBuild,
      )
      if (target.code !== normalizeRoomCode(code)) return { name: 'notFound', path }
      const host = local.searchParams.get('host')
      if (host) target.hostUserId = host
      return { name: 'viewer', code: target.code, target }
    } catch {
      return { name: 'notFound', path }
    }
  }

  return { name: 'notFound', path }
}

/** Reactive current route. Mutate via `navigate`, never directly. */
export const router = $state<{ current: Route }>({
  current: parseRoute(typeof location === 'undefined' ? '/' : location.pathname + location.search),
})

/** Push (or replace) a history entry and re-render. */
export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (options.replace) {
    history.replaceState(null, '', to)
  } else {
    history.pushState(null, '', to)
  }
  router.current = parseRoute(to)
}

/** Build the canonical viewer URL for a room code. */
export function viewerPath(code: string, target?: RoomTarget): string {
  return `/r/${encodeURIComponent(code)}${target ? `?room=${encodeURIComponent(target.shareUrl)}${target.hostUserId ? `&host=${encodeURIComponent(target.hostUserId)}` : ''}` : ''}`
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    router.current = parseRoute(location.pathname + location.search)
  })

  // Intercept plain left-clicks on same-origin links so `<a href="/host">`
  // behaves like a SPA link without every component needing an onclick.
  window.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const anchor = (event.target as Element | null)?.closest?.('a')
    if (!anchor) return

    const href = anchor.getAttribute('href')
    if (!href || href.startsWith('http') || href.startsWith('//')) return
    if (anchor.hasAttribute('target') && anchor.getAttribute('target') !== '_self') return
    if (anchor.hasAttribute('download')) return

    event.preventDefault()
    navigate(href)
  })
}
